import iconNuvemshopWhite from '@/assets/icons/icon-nuvemshop-white.png';
import iconShopifyWhite from '@/assets/icons/icon-shopify-white.png';
import type { ReactNode } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import { ExternalLinkButton } from './external-link-button';
import { Button } from './ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';

type Store = 'nuvemshop' | 'shopify';

type IntegrationDialogProps = {
  store: Store;
  activateIntegrationUrl?: string | null;
  /** Element that opens the dialog; defaults to the "set up integration" button. */
  trigger?: ReactNode;
};

export default function IntegrationDialog({
  store,
  activateIntegrationUrl,
  trigger,
}: IntegrationDialogProps) {
  const { LL } = useI18nContext();

  const maskIcon = (iconUrl: string) => (
    <div
      className="h-4 w-4 bg-current"
      style={{
        maskImage: `url(${iconUrl})`,
        maskSize: 'contain',
      }}
    />
  );

  const storeConfig =
    store === 'nuvemshop'
      ? {
          triggerLabel: LL.setUpNuvemshopIntegration(),
          dialogDescription: LL.nuvemshopIntegrationDialogDescription(),
          icon: maskIcon(iconNuvemshopWhite),
        }
      : {
          triggerLabel: LL.setUpShopifyIntegration(),
          dialogDescription: LL.theShopifyIntegrationHasToBeManually(),
          icon: maskIcon(iconShopifyWhite),
        };

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button className="flex flex-row items-center gap-2">
            {storeConfig.icon}
            {storeConfig.triggerLabel}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{LL.integrationDialogTitle()}</DialogTitle>
          <DialogDescription>{storeConfig.dialogDescription}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{LL.campaigns.cancelButton()}</Button>
          </DialogClose>
          <ExternalLinkButton
            className="flex flex-row items-center gap-2"
            href={activateIntegrationUrl ?? ''}
          >
            {LL.common.continue()}
          </ExternalLinkButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
